import { AxiosInstance, AxiosResponse } from "axios"
import {
  ScriptExecutionParameters,
  AvailableScriptsResponse,
  RunAutomationScriptResponse,
  JobStatusResponse,
  ScriptMetadata,
  OutputProject,
} from "types/automation.types"

/**
 * Fetches the available scripts for a unit.
 *
 * @param axiosInstance - Authenticated axios instance
 * @returns Promise containing the list of available script files as a string
 * @throws Will throw an error if the API call fails.
 */
export async function getAutomationScripts(
  axiosInstance: AxiosInstance
): Promise<AxiosResponse<AvailableScriptsResponse>> {
  const response = await axiosInstance.get<AvailableScriptsResponse>(
    `/available-scripts`
  )
  return response
}

/**
 * Fetches the metadata for a specific automation script.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param script - The name of the script for which parameters are to be fetched.
 * @returns Promise containing the parameters of the script as a metadata object
 * @throws Will throw an error if the API call fails.
 */

export async function getAutomationScriptMetadata(
  axiosInstance: AxiosInstance,
  script: string
): Promise<AxiosResponse<ScriptMetadata>> {
  const response = await axiosInstance.get<ScriptMetadata>(
    `/script-metadata-get?name=${script}&access=private`
  )
  return response
}

/**
 * Run a specific automation script with given parameters.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param script - The name of the script to be run.
 * @param parameters - The parameters to be passed to the script.
 * @param outputProject - The output project information (project_id and owner)
 * @returns Promise containing the response of the script execution
 * @throws Will throw an error if the API call fails.
 */
export async function runAutomationScript(
  axiosInstance: AxiosInstance,
  script: string,
  parameters: ScriptExecutionParameters,
  outputProject?: OutputProject
): Promise<AxiosResponse<RunAutomationScriptResponse>> {
  // Build query parameters
  const queryParams = new URLSearchParams({
    name: script,
  })

  // Add output project parameters if provided
  if (outputProject) {
    queryParams.append("output_project_id", outputProject.project_id)
    queryParams.append("output_project_owner", outputProject.owner)
  }

  const response = await axiosInstance.post(
    `/automation-script-run?${queryParams.toString()}`,
    {
      parameters: parameters,
    }
  )
  return response
}

/**
 * Fetches the list of automation jobs.
 *
 * @param axiosInstance - Authenticated axios instance
 * @returns Promise containing the list of automation jobs as a string
 * @throws Will throw an error if the API call fails.
 */
export async function getJobsList(
  axiosInstance: AxiosInstance
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(`/automation-job-list`)
  return response
}

/**
 * Fetches the list of automation jobs.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param jobID - The name of the script to be run. From the list of jobs/result of runAutomationScript.
 * @returns Promise containing the list of automation jobs as a string
 * @throws Will throw an error if the API call fails.
 */
export async function getJobStatus(
  axiosInstance: AxiosInstance,
  jobID: string
): Promise<AxiosResponse<JobStatusResponse>> {
  const response = await axiosInstance.get(
    `/automation-job-status?job_id=${jobID}`
  )
  return response
}

/**
 * !Fetches the result of a specific automation job. MAYBE NOT NEEDED AS Status contains the result and the output is in a folder
 *
 * @param axiosInstance - Authenticated axios instance
 * @param jobID - The ID of the job whose result is to be fetched.
 * @returns Promise containing the job result as a string
 * @throws Will throw an error if the API call fails.
 */
export async function getJobResult(
  axiosInstance: AxiosInstance,
  jobID: string
): Promise<AxiosResponse<JobStatusResponse>> {
  const response = await axiosInstance.get(
    `/automation-job-result?job_id=${jobID}`
  )
  return response
}

/**
 * Cancel a specific automation job.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param jobID - The ID of the job to be deleted.
 * @returns Promise containing the response of the delete operation
 * @throws Will throw an error if the API call fails.
 */
export async function cancelJob(
  axiosInstance: AxiosInstance,
  jobID: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(
    `/automation-job-cancel?job_id=${jobID}`
  )
  return response
}
